import { ReactNode, useState } from 'react'
import { DropdownProps } from 'shared/components/Dropdown/Dropdown'
import { Option } from 'shared/components/Dropdown/Dropdown.styles'
import { MoreDropdown } from 'shared/components/Dropdown/MoreDropdown'
import { InlineSpinner } from 'shared/components/Spinner'
import { Handler } from 'shared/helpers/typeHelper'
import { royalBlue } from 'shared/theme/colors'
import styled from 'styled-components'

import { useUser } from '../../helpers/useUser'
import { DriverTransitionButton } from '../FSM/Buttons/DriverTransitionButton'
import { getDriverTransitionProps } from '../FSM/Transitions/DriverFSMTransitions'
import { UserReset } from './UserReset'

interface UserDriverTransitionDropdownProps {
  userId: string
  permissions: any
  onSuccess: Handler
}

export function UserDriverTransitionDropdown({
  userId,
  permissions,
  onSuccess,
}: UserDriverTransitionDropdownProps) {
  const [isTransitionLoading, setIsTransitionLoading] = useState<ReactNode | null>(null)
  const [enableUserLoad, setEnableUserLoad] = useState(false)
  const { user, isLoading, refetch } = useUser(userId, { enabled: enableUserLoad })

  function getCardMoreOptions() {
    if (!enableUserLoad || isLoading || !user) {
      return [
        {
          label: 'load',
          renderer: () => <InlineSpinner executeOnLoad={() => setEnableUserLoad(true)} size="1em" />,
        },
      ]
    }

    const transitions = getDriverTransitionProps(user)

    const options: DropdownProps<unknown>['options'] =
      transitions?.map((transition) => ({
        label: transition.transitionString,
        renderer: (option, closeDropdown) => (
          <DriverTransitionButton
            record={user}
            transitionProps={transition}
            loading={Boolean(isTransitionLoading)}
            setLoading={(isLoading) => setIsTransitionLoading(isLoading ? option.label : null)}
            onSuccess={async () => {
              await onSuccess?.()
              await refetch()
              closeDropdown()
            }}
          >
            <Option key="1">
              {option.label}
              {isTransitionLoading === option.label && <InlineSpinner size="1em" />}
            </Option>
          </DriverTransitionButton>
        ),
      })) || []

    const resetOption = (
      <UserReset record={user} permissions={permissions}>
        {(handleReset) => (
          <Option key="2" onClick={() => handleReset()}>
            Reset
          </Option>
        )}
      </UserReset>
    )

    if (resetOption) {
      options.push({
        label: 'reset',
        renderer: () => resetOption,
      })
    }

    return options
  }

  return (
    <StyledMoreDropdown
      options={getCardMoreOptions()}
      ariaLabel="Transition driver"
      align="left"
      width={200}
      maxHeight={400}
    />
  )
}

const StyledMoreDropdown = styled(MoreDropdown)`
  svg,
  svg:hover {
    color: ${royalBlue};
  }
`
